import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { ComponentType, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

const defaultIconSx: SxProps<Theme> = {
  fontSize: 80,
  color: 'text.disabled',
  mb: 2,
  opacity: 0.5,
};

export interface EmptyStateProps {
  /** Icon component (e.g. SensorsOffIcon). Styled with default sx (size, color, opacity). Defaults to InboxOutlined. */
  icon?: ComponentType<SvgIconProps>;
  /**
   * Main message/heading (e.g. FormattedMessage or string). Wrapped in Typography h6.
   * Defaults to "Sin resultados" when omitted. Pass `false` to render no heading.
   */
  message?: ReactNode | false;
  /** Optional secondary help (e.g. FormattedMessage or string). Wrapped in Typography body2. Pass null to hide. */
  help?: ReactNode | null;
  /** Optional action (e.g. Button) rendered below the help text. */
  action?: ReactNode;
  /** Whether to center the content. Defaults to false. */
  centered?: boolean;
}

/**
 * Empty state shown when a list or view has no content.
 * Customizable via icon, message, and optional help text.
 * Default content targets list "no results" (e.g. due to filters).
 */
export const EmptyState = ({ icon, message, help, action, centered = false }: EmptyStateProps) => {
  const IconComponent = icon ?? InboxOutlinedIcon;
  const iconNode = <IconComponent sx={defaultIconSx} />;

  const messageNode =
    message === false ? null : message !== undefined ? (
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {message}
      </Typography>
    ) : (
      <Typography variant="h6" color="text.secondary" gutterBottom>
        <FormattedMessage id="common.listView.noResults" defaultMessage="Sin resultados" />
      </Typography>
    );

  const helpNode =
    help === undefined ? (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, mb: 4 }}>
        <FormattedMessage
          id="common.listView.tryOtherFiltersOrClear"
          defaultMessage="Prueba buscando con otros filtros o elimina los aplicados para ver más resultados."
        />
      </Typography>
    ) : help !== null ? (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, mb: 4 }}>
        {help}
      </Typography>
    ) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: centered ? 0 : 14, // Significant vertical padding to center it visually
        height: centered ? '100%' : 'auto',
        width: centered ? '100%' : 'auto',
      }}
    >
      {iconNode}
      {messageNode}
      {helpNode}
      {action}
    </Box>
  );
};
